import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Table,
  Switch,
  InputNumber,
} from "antd";
import "antd/dist/reset.css";
import { getColumns } from "./modules/getColumns";
import { useRatePlan } from "./modules/useRatePlan";

const { Option } = Select;

const RatePlanManagementTemplate = () => {
  const {
    ratePlans,
    rooms,
    cancellationPolicies,
    loading,
    isModalVisible,
    editingRatePlan,
    handleCreate,
    handleEdit,
    handleDelete,
    handleFormSubmit,
    setIsModalVisible,
    form,
  } = useRatePlan();
  const columns = getColumns({ rooms, handleDelete, handleEdit });

  return (
    <div className="container">
      <h1>Rate Plan Management</h1>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleCreate}
        style={{ marginBottom: 16 }}
      >
        Create Rate Plan
      </Button>
      <Table
        dataSource={ratePlans}
        columns={columns}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1000 }} // Enables horizontal scrolling for smaller screens
      />

      <Modal
        title={editingRatePlan ? "Edit Rate Plan" : "Create Rate Plan"}
        visible={isModalVisible}
        onOk={handleFormSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="rateplanname"
            label="Rate Plan Name"
            rules={[{ required: true, message: "Please enter rate plan name" }]}
          >
            <Input placeholder="Enter Rate Plan Name" />
          </Form.Item>

          <Form.Item
            name="room"
            label="Select Room(s)"
            rules={[{ required: true, message: "Please select room(s)" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select Room(s)"
              onChange={(value) => {
                const currentPricing =
                  form.getFieldValue("rateplanpricing") || [];
                const updatedPricing = value.map((roomId) => {
                  const existing = currentPricing.find(
                    (rp) => rp.roomId === roomId
                  );
                  return existing
                    ? existing
                    : {
                        roomId,
                        deriveFromRentperdayByDate: false,
                        manualRate: {},
                      };
                });
                form.setFieldsValue({ rateplanpricing: updatedPricing });
              }}
            >
              {Array.isArray(rooms) &&
                rooms.map((room) => (
                  <Option key={room._id} value={room._id}>
                    {room.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="cancellationPolicy"
            label="Cancellation Policy"
            rules={[
              {
                required: true,
                message: "Please select a cancellation policy",
              },
            ]}
          >
            <Select placeholder="Select Cancellation Policy">
              {Array.isArray(cancellationPolicies) &&
              cancellationPolicies.length > 0 ? (
                cancellationPolicies.map((policy, index) => {
                  // Remove _id from cancellationPolicy
                  const { _id, ...cancellationPolicy } = policy || {};
                  return (
                    <Option
                      key={index}
                      value={JSON.stringify(cancellationPolicy)}
                    >
                      {cancellationPolicy.chargeType} -{" "}
                      {cancellationPolicy.chargePercentage}% charge
                      {cancellationPolicy.chargeType === "Flexible"
                        ? ` if canceled ${cancellationPolicy.daysBeforeCheckin} days before check-in`
                        : ""}
                    </Option>
                  );
                })
              ) : (
                <Option disabled>No cancellation policies available</Option>
              )}
            </Select>
          </Form.Item>

          <Form.Item
            name="mealPlan"
            label="Meal Plan"
            rules={[{ required: true, message: "Please select a meal plan" }]}
          >
            <Select placeholder="Select Meal Plan">
              <Option value="None">None</Option>
              <Option value="Breakfast">Breakfast</Option>
              <Option value="Half Board">Half Board</Option>
              <Option value="Full Board">Full Board</Option>
            </Select>
          </Form.Item>

          {/* Discount, Minimum Stay and Maximum Stay removed per UX update */}

          <Form.Item
            name="ratePlanSetup"
            label="Rate Plan Setup"
            rules={[
              { required: true, message: "Please select rate plan setup" },
            ]}
          >
            <Select placeholder="Select Rate Plan Setup">
              <Option value="Manual">Manual</Option>
              <Option value="Derived">Derived</Option>
            </Select>
          </Form.Item>

          {/* Show discount only when Derived is selected */}
          <Form.Item
            shouldUpdate={(prev, cur) =>
              prev.ratePlanSetup !== cur.ratePlanSetup
            }
          >
            {() => {
              const setup = form.getFieldValue("ratePlanSetup");
              return setup === "Derived" ? (
                <Form.Item
                  name="discount"
                  label={
                    <div>
                      Discount (%){" "}
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#888",
                        }}
                      >
                        (Note: Derived from the room's race rate.)
                      </span>
                    </div>
                  }
                  rules={[
                    {
                      type: "number",
                      min: 0,
                      max: 100,
                      message: "Enter discount between 0 and 100",
                    },
                  ]}
                >
                  <InputNumber style={{ width: "100%" }} min={0} max={100} />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>

          <Form.Item name="ratePlanDescription" label="Rate Plan Description">
            <Input.TextArea
              placeholder="Enter Rate Plan Description"
              rows={3}
            />
          </Form.Item>

          {editingRatePlan && (
            <Form.Item name="closeout" valuePropName="checked" label="Closeout">
              <Switch
                onChange={(checked) =>
                  form.setFieldsValue({ closeout: checked })
                }
              />
            </Form.Item>
          )}

          <Form.Item name="extraBenefits" label="Extra Benefits">
            <Select mode="tags" placeholder="Add Extra Benefits" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RatePlanManagementTemplate;
